import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

const Stats = () => {

  const navigate = useNavigate();
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const handleButtonSeeHowItWorks = () => {
    navigate('/get-started');
  };

  const cards = [
    {
      icon: "/assets/stat1.svg",
      value: t('stats.card1Value'),
      label: t('stats.card1Label'),
      bg: "bg-[#007B83] text-white",
    },
    {
      icon: "/assets/stat2.svg",
      value: t('stats.card2Value'),
      label: t('stats.card2Label'),
      bg: "bg-[#E3F3FA] text-[#1A1A1A]",
    },
    {
      icon: "/assets/stat3.svg",
      value: t('stats.card3Value'),
      label: t('stats.card3Label'),
      bg: "bg-[#E9F9FA] text-[#1A1A1A]",
    },
    {
      icon: "/assets/stat4.svg",
      value: t('stats.card4Value'),
      label: t('stats.card4Label'),
      bg: "bg-[#1C99DE] text-white",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, x: -50, scale: 0.9 },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const slideInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <section
      ref={ref}
      className="bg-gradient-to-br from-[#01585D] to-[#007F87] py-16 md:py-24"
    >
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 lg:gap-20 xl:gap-28 items-center">
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 gap-6 order-2 lg:order-1"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            {cards.map((c, i) => (
              <motion.div
                key={i}
                className={`rounded-2xl py-8 px-6 md:py-12 md:px-8 shadow-xl ${c.bg}`}
                style={{
                  boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                }}
                variants={cardVariants}
              >
                <div>
                  <img src={c.icon} alt={c.label} />
                </div>
                <div className="space-y-3 mt-6">
                  <p
                    className={`text-3xl md:text-4xl font-extrabold ${
                      c.bg.includes("text-[#1A1A1A]")
                        ? "text-[#1A1A1A]"
                        : "text-white"
                    }`}
                  >
                    {c.value}
                  </p>
                  <p
                    className={`${
                      c.bg.includes("text-[#1A1A1A]")
                        ? "text-[#5A5A59]"
                        : "text-white/80"
                    }`}
                  >
                    {c.label}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Right: Heading and copy */}
          <motion.div
            className="text-white space-y-6 order-1 lg:order-2"
            variants={slideInRight}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            <motion.div
              className="bg-[#FFFFFF] px-4 py-2 rounded-full w-fit border"
              variants={fadeIn}
            >
              <span className="text-sm text-[#5A5A59]">{t('stats.badge')}</span>
            </motion.div>

            <motion.h2
              className="text-[60px] font-semibold leading-tight"
              variants={fadeIn}
            >
              {t('stats.heading')}
            </motion.h2>

            <motion.p
              className="text-[#FFFFFF] text-[16px] max-w-xl"
              variants={fadeIn}
            >
              {t('stats.description')}
            </motion.p>

            <motion.button className="btn-primary" variants={fadeIn} onClick={handleButtonSeeHowItWorks}>
              {t('stats.button')}
            </motion.button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Stats;
